import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AppModal,
  AppCheckbox,
  AppSelect,
  AppTabs,
  AppButton,
  AppRadio,
  AppTextArea,
  AppTextField,
} from '@/components/atoms';
import useAppModal from '@/hooks/useAppModal';
import { FORM_MESSAGES } from '@/constants/formMessages';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  R2_HOT_TREATMENT_TYPE_ID,
  R2_MASSAGE_PART_OPTIONS,
  R2_ORTHOMORPHIA_PART_OPTIONS,
} from '@/constants/consentForm';
import { formatJapaneseDate } from '@/utils/dateFormat';
import { joinR2Csv, resolveR2TreatmentPartLabelsFromStoredConsent } from '@/utils/consentForm';
import type {
  PendingVisitData,
  VisitClinicMassager,
  VisitClinicConsent,
  VisitClinicDayVisit,
  VisitClinicRezept,
} from '@/types/visitClinic.types';
import VisitErrorMessageList from '@/components/molecules/VisitErrorMessageList';
import { useMeisaiIssueVisitStatus } from '@/hooks/main/useMeisaiIssueVisitStatus';
import { MEISAI_ISSUE_TYPE_DAILY } from '@/services/main/meisaiIssueSetting';
import {
  resolveIsEarliestOfMonth,
  type StagedVisit,
  LAW_REVISION_DATE_2026,
} from './meisaiEarliestVisit';
import {
  hasConsentDifferenceReason,
  resolveRezeptForDay,
  revealConsentDifferenceReason,
} from './consentDifferenceReason';

// BD 1.3.x error #24 (customer FB 195): the locked-period error reads the same as the lock notice.
const LOCKED_EDIT_DELETE_MESSAGE = 'ロックされているため、編集及び削除はできません。';

interface OutcomeTypeOption {
  id: number;
  name: string;
}

export interface VisitClinicDetailModalProps {
  /** 令和8年法改正 No 5 — reads the patient's 明細書発行設定 for the visit's month. */
  patientId?: number | null;
  isOpen: boolean;
  onClose: () => void;
  /** YYYY-MM-DD */
  date: string;
  current: PendingVisitData;
  /** Server-side visit record for the selected day (往診編集 readonly fields). */
  rawVisit?: VisitClinicDayVisit | null;
  /**
   * 令和8年法改正 No 5 — the month's unsaved rows, so 「is this the month's earliest visit?」
   * is answered from what the calendar currently shows rather than from what is saved.
   */
  stagedVisits?: StagedVisit[];
  /** Ids of saved visits the calendar has queued for deletion. */
  deletedVisitIds?: number[];
  massagers: VisitClinicMassager[];
  outcomeTypes: OutcomeTypeOption[];
  consents: VisitClinicConsent[];
  /**
   * R2 (HIT-15106) — the month's 料金 tables with what their 「当月施術内容が同意書と異なる理由」
   * boxes show right now (unsaved typing included): the reminder on 適用 is skipped when the
   * edited day's table already has a reason.
   */
  rezepts?: VisitClinicRezept[];
  /** Whether the selected day is the first_treatment_date (初療日) */
  isFirstTreatmentDay?: boolean;
  /** BD §2.1: initial inspection fee prices (single はり or きゅう / combined both) */
  initialInspectionPrices?: { single: number; combined: number };
  /** Apply changes to parent's pending state */
  onApply: (update: Partial<PendingVisitData>) => void;
  serviceType: string;
  /** Locked or error → show message + disable all + 適用 just closes without saving */
  disabled?: boolean;
  isLocked?: boolean;
}

const LABEL_BG = {
  acupuncture: '#FFFF99',
  moxibustion: '#CCFFCC',
  electric: '#FFCC99',
} as const;

// HIT-15106 (BD 1.3.2 §2.1, customer wording 2026/10/30).
const CONSENT_DIFFERENT_TREATMENT_WARNING =
  '同意書の施術の種類・部位と異なる施術をする場合は、「当月施術内容が同意書と異なる理由」欄に理由を入力してください。';
const NO_CONSENT_FOR_TREATMENT_DATE_MESSAGE = '施術日に対応する同意書がありません。';
const DISTANCE_MEASUREMENT_FAILED_MESSAGE = '距離が測定できません。';
const R2_TREATMENT_PART_REQUIRED_MESSAGE =
  '【マッサージ】または【変形徒手矯正】の部位をチェックしてください。';

function areSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function hasDistanceMeasurementFailed(rawVisit?: VisitClinicDayVisit | null): boolean {
  const distance = rawVisit?.distance;
  if (distance !== null && distance !== undefined && distance !== '') {
    const numericDistance = Number(distance);
    if (!Number.isFinite(numericDistance) || numericDistance < 0) return true;
  }

  return (rawVisit?.error_items ?? []).some(item =>
    [
      'visits_data_distance_warning',
      'visits_data_distance_warning_unknown',
      'visits_data_distance_both_error',
    ].includes(item.code)
  );
}

function formatVisitTime(value?: string | null): string {
  if (!value) return '';
  const timeMatch = String(value).match(/[T\s](\d{1,2}):(\d{2})/);
  if (timeMatch) {
    return `${Number(timeMatch[1])}時${timeMatch[2]}分`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getHours()}時${String(date.getMinutes()).padStart(2, '0')}分`;
}

function formatVisitMinutes(value?: number | null): string {
  if (value == null) return '';
  return `${value}分`;
}

function hasConsentOnDate(date: string, consents: VisitClinicConsent[]): boolean {
  return consents.some(c => {
    if (!c.treatment_term_from || !c.treatment_term_to) return false;
    return date >= c.treatment_term_from && date <= c.treatment_term_to;
  });
}

function consentAllows(
  date: string,
  consents: VisitClinicConsent[],
  key: 'has_acupuncture' | 'has_moxibustion' | 'has_electric',
): boolean {
  return consents.some(c => {
    if (!c.treatment_term_from || !c.treatment_term_to) return false;
    if (date < c.treatment_term_from || date > c.treatment_term_to) return false;
    return c[key] === undefined || c[key] === true;
  });
}

function getR2HotTreatmentTypeForDate(date: string, consents: VisitClinicConsent[]): number | null {
  const covering = consents.filter(c => {
    if (!c.treatment_term_from || !c.treatment_term_to) return false;
    return date >= c.treatment_term_from && date <= c.treatment_term_to;
  });
  if (covering.length === 0) return null;

  const hotType = covering
    .map(c => Number(c.hot_treatment_type_id ?? 0))
    .find(
      typeId =>
        typeId === R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS ||
        typeId === R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS_ELECTRO,
    );

  return hotType ?? null;
}

function getCoveringR2Consents(date: string, consents: VisitClinicConsent[]): VisitClinicConsent[] {
  return consents.filter(c => {
    if (!c.treatment_term_from || !c.treatment_term_to) return false;
    return date >= c.treatment_term_from && date <= c.treatment_term_to;
  });
}

function getR2MassagePartsForDate(date: string, consents: VisitClinicConsent[]): Set<string> {
  const out = new Set<string>();
  getCoveringR2Consents(date, consents).forEach(c => {
    resolveR2TreatmentPartLabelsFromStoredConsent(c.massage ?? null, null).forEach(part =>
      out.add(part)
    );
  });
  return out;
}

/**
 * HIT-15106 (BD 1.3.2 S26): a マッサージ box is enabled for a limb on the 同意書's
 * 「マッサージ」 row OR its 「マッサージ＋変形徒手矯正術」 row (stored in orthomorphia, so it has
 * no 躯幹). Ticking it then clears that limb's 変形徒手 box (toggleR2Part).
 */
function getR2MassageEnabledPartsForDate(date: string, consents: VisitClinicConsent[]): Set<string> {
  const out = new Set<string>();
  getCoveringR2Consents(date, consents).forEach(c => {
    resolveR2TreatmentPartLabelsFromStoredConsent(c.massage ?? null, c.orthomorphia ?? null).forEach(
      part => out.add(part)
    );
  });
  return out;
}

function getR2OrthomorphiaPartsForDate(date: string, consents: VisitClinicConsent[]): Set<string> {
  const out = new Set<string>();
  getCoveringR2Consents(date, consents).forEach(c => {
    resolveR2TreatmentPartLabelsFromStoredConsent(null, c.orthomorphia ?? null).forEach(part =>
      out.add(part)
    );
  });
  return out;
}

/** Resolve license flags from the currently selected massager (draft.massager_id) */
function getMassagerLicense(
  massagerId: number,
  massagers: VisitClinicMassager[],
): { hasAcupuncture: boolean; hasMoxibustion: boolean; hasMassage: boolean } {
  const m = massagers.find(x => x.massager_id === massagerId);
  return {
    hasAcupuncture: m?.has_acupuncture_license ?? true,
    hasMoxibustion: m?.has_moxibustion_license ?? true,
    hasMassage: m?.has_massage_license ?? true,
  };
}

const VisitClinicDetailModal: React.FC<VisitClinicDetailModalProps> = ({
  patientId,
  isOpen,
  onClose,
  date,
  current,
  rawVisit,
  stagedVisits = [],
  deletedVisitIds = [],
  massagers,
  outcomeTypes,
  consents,
  rezepts = [],
  isFirstTreatmentDay = false,
  initialInspectionPrices,
  onApply,
  serviceType,
  disabled = false,
  isLocked = false,
}) => {
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const modal = useAppModal();
  const isR2 = serviceType?.toUpperCase() === SERVICE_TYPE_R2;
  const hasVisitError = !!rawVisit?.is_error;
  const hasDistanceError = hasDistanceMeasurementFailed(rawVisit);

  const isHouseCall =
    current.previous_visit_id !== null && current.previous_visit_id !== -1;

  const [draft, setDraft] = useState<PendingVisitData>(current);
  const [isDirty, setIsDirty] = useState(false);
  const [r2MassageTouched, setR2MassageTouched] = useState(false);
  const [r2OrthomorphiaTouched, setR2OrthomorphiaTouched] = useState(false);

  // HIT-11715: seed the draft from the stored value as-is. 初検料 is defaulted to ON
  // only when a new day is created on the 初療日 (calendar click), so a user's
  // uncheck must survive reopening the modal (A2 schedule.blade.php:473-476).
  useEffect(() => {
    if (isOpen) {
      const init = { ...current };
      if (isR2) {
        if (init.massage === null || init.massage === undefined) {
          init.massage = joinR2Csv(Array.from(getR2MassagePartsForDate(date, consents)));
        }
        if (init.orthomorphia === null || init.orthomorphia === undefined) {
          init.orthomorphia = joinR2Csv(Array.from(getR2OrthomorphiaPartsForDate(date, consents)));
        }
      }
      setDraft(init);
      setIsDirty(false);
      setR2MassageTouched(false);
      setR2OrthomorphiaTouched(false);
    }
  }, [isOpen, current, isR2, date, consents]);

  // BD §2.7: re-check license when 施術担当者 changes
  const { hasAcupuncture, hasMoxibustion, hasMassage } = useMemo(
    () => getMassagerLicense(draft.massager_id, massagers),
    [draft.massager_id, massagers],
  );

  // BD §2.7: when a treatment is disabled (license/consent), it is treated as
  // unchecked for 初検料 (2.1) + 適用 payload, but the original draft value is
  // preserved so switching back to a licensed massager restores the state.

  const hasConsent = useMemo(() => hasConsentOnDate(date, consents), [date, consents]);
  const allDisabled = disabled || isLocked || hasVisitError || !hasConsent;

  const acupunctureDisabled =
    allDisabled || !hasAcupuncture || !consentAllows(date, consents, 'has_acupuncture');
  const moxibustionDisabled =
    allDisabled || !hasMoxibustion || !consentAllows(date, consents, 'has_moxibustion');
  const electricDisabled =
    allDisabled || !consentAllows(date, consents, 'has_electric') || (acupunctureDisabled && moxibustionDisabled);
  const r2HotTreatmentType = useMemo(
    () => getR2HotTreatmentTypeForDate(date, consents),
    [date, consents],
  );
  // What the 同意書 itself says for the 「マッサージ」 row — the reminder compares against this.
  const r2ConsentMassageParts = useMemo(
    () => getR2MassagePartsForDate(date, consents),
    [date, consents],
  );
  const r2MassageEnabledParts = useMemo(
    () => getR2MassageEnabledPartsForDate(date, consents),
    [date, consents],
  );
  const r2OrthomorphiaAllowedParts = useMemo(
    () => getR2OrthomorphiaPartsForDate(date, consents),
    [date, consents],
  );
  const r2CoreTreatmentDisabled =
    !hasConsent || !hasMassage || (r2MassageEnabledParts.size === 0 && r2OrthomorphiaAllowedParts.size === 0);
  const r2MassageParts = useMemo(
    () => resolveR2TreatmentPartLabelsFromStoredConsent(draft.massage ?? null, null),
    [draft.massage],
  );
  // HIT-15106 (BD 1.3.2 S37/S38): 温罨法 / 温罨法・電療 go with マッサージ only — with no
  // マッサージ box ticked they are disabled and OFF, even if 変形徒手 is still ticked.
  const r2NoMassagePart = r2MassageParts.size === 0;
  const r2HotTreatmentDisabled =
    allDisabled || r2CoreTreatmentDisabled || r2NoMassagePart || (hasConsent && r2HotTreatmentType === null);
  const effR2NotHotTreatment = r2NoMassagePart ? true : !!draft.is_not_hot_treatment;
  const r2MassageDiffersFromConsent = useMemo(
    () => !areSetsEqual(r2MassageParts, r2ConsentMassageParts),
    [r2MassageParts, r2ConsentMassageParts],
  );
  const r2OrthomorphiaParts = useMemo(
    () => resolveR2TreatmentPartLabelsFromStoredConsent(null, draft.orthomorphia ?? null),
    [draft.orthomorphia],
  );
  const r2OrthomorphiaDiffersFromConsent = useMemo(
    () => !areSetsEqual(r2OrthomorphiaParts, r2OrthomorphiaAllowedParts),
    [r2OrthomorphiaParts, r2OrthomorphiaAllowedParts],
  );

  // Effective values: a disabled treatment is treated as 0 (for 初検料 calc + 適用 payload)
  // but the underlying draft value is preserved so switching massager back restores it.
  const effAcupuncture = acupunctureDisabled ? 0 : draft.is_acupuncture;
  const effMoxibustion = moxibustionDisabled ? 0 : draft.is_moxibustion;
  const effElectric = electricDisabled ? 0 : draft.electric_treatment;

  const massagerOptions = useMemo(
    () =>
      massagers
        .filter(m => {
          const isSelected =
            m.massager_id === draft.massager_id &&
            m.clinic_location_id === draft.clinic_location_id;
          if (isSelected) return true;
          const fromOk = !m.available_from || m.available_from <= date;
          const toOk = !m.available_to || m.available_to > date;
          return fromOk && toOk;
        })
        .map(m => ({ value: m.id, label: m.label })),
    [massagers, draft.massager_id, draft.clinic_location_id, date],
  );

  const outcomeOptions = useMemo(
    () => outcomeTypes
      .filter(o => !isR2 || [1, 2, 3].includes(Number(o.id)))
      .map(o => ({ value: o.id, label: o.name })),
    [outcomeTypes, isR2],
  );

  const update = <K extends keyof PendingVisitData>(key: K, value: PendingVisitData[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const toggleR2Part = (
    key: 'massage' | 'orthomorphia',
    currentParts: Set<string>,
    part: string,
    checked: boolean,
  ) => {
    const next = new Set(currentParts);
    if (checked) {
      next.add(part);
    } else {
      next.delete(part);
    }
    let nextMassage = r2MassageParts;
    let nextOrthomorphia = r2OrthomorphiaParts;
    if (key === 'massage') {
      setR2MassageTouched(true);
      nextMassage = next;
      if (checked) {
        nextOrthomorphia = new Set(r2OrthomorphiaParts);
        nextOrthomorphia.delete(part);
      }
    } else {
      setR2OrthomorphiaTouched(true);
      nextOrthomorphia = next;
      if (checked) {
        nextMassage = new Set(r2MassageParts);
        nextMassage.delete(part);
      }
    }
    setDraft(prev => ({
      ...prev,
      massage: joinR2Csv(Array.from(nextMassage)),
      orthomorphia: joinR2Csv(Array.from(nextOrthomorphia)),
      // HIT-15106: the last マッサージ box going off turns 温罨法 off. Ticking a
      // マッサージ box again only re-enables it; the operator ticks it back by hand —
      // also when the popup opened with no マッサージ box on (OFF was only derived then).
      ...(nextMassage.size === 0 || r2MassageParts.size === 0 ? { is_not_hot_treatment: true } : {}),
    }));
    setIsDirty(true);
  };

  // BD §2.3-2.4: はり+きゅう at least one required
  const handleApply = useCallback(() => {
    if (allDisabled) {
      onClose();
      return;
    }
    if (!isR2 && !effAcupuncture && !effMoxibustion) {
      // BD §2.3-2.4: popup system dialog, keep modal open, don't save.
      // Applies to both 詳細編集 and 往診編集 flows — is_acupuncture / is_moxibustion
      // are saved on the visit regardless of house-call state.
      modal.warning({
        title: 'はり・きゅうのどちらかにチェックをいれてください。',
        okText: 'OK',
      });
      return;
    }
    if (isR2 && r2MassageParts.size === 0 && r2OrthomorphiaParts.size === 0) {
      modal.warning({
        title: R2_TREATMENT_PART_REQUIRED_MESSAGE,
        okText: 'OK',
      });
      return;
    }
    const applyChanges = () => {
      onApply({
      // A2 parity: massager_id is NOT updated for 往診 visits (previous_visit_id != -1)
      ...(!isHouseCall && {
        massager_id: draft.massager_id,
        clinic_location_id: draft.clinic_location_id,
      }),
      ...(!isR2 && {
        is_initial_inspection_fee: draft.is_initial_inspection_fee,
        is_acupuncture: effAcupuncture,
        is_moxibustion: effMoxibustion,
        electric_treatment: effElectric,
      }),
      ...(isR2 && {
        is_not_hot_treatment: effR2NotHotTreatment,
        massage: draft.massage ?? '',
        orthomorphia: draft.orthomorphia ?? '',
      }),
      outcome_type_id: draft.outcome_type_id,
      massager_remarks: draft.massager_remarks,
      // 令和8年法改正 No 5 — the value always travels so the staged row shows what the box
      // showed; omitting it left the row blank and reopening read that back as unchecked.
      // `is_meisai_issue_user_changed` is the separate question of who decided: only a hand
      // toggle sets it, and without it the server ignores the value and keeps deriving from
      // the patient's 明細書発行設定 — which is what pins 月別 to the month's earliest visit.
      is_meisai_issue: draft.is_meisai_issue,
      ...(draft.is_meisai_issue_user_changed
        ? { is_meisai_issue_user_changed: draft.is_meisai_issue_user_changed }
        : {}),
      });
      onClose();
    };

    // HIT-15106 (BD 1.3.2 §2.1): the reminder only shows while the edited day's 料金 table
    // has no reason yet (what the box shows now, before 登録). OK then takes the operator
    // to that box. It never blocks 適用.
    const consentDifferenceRezept = isR2
      ? resolveRezeptForDay(date, rawVisit?.rezept_id ?? null, rezepts)
      : null;
    const shouldShowConsentDifferenceWarning =
      isR2 &&
      (
        (r2MassageTouched && r2MassageDiffersFromConsent) ||
        (r2OrthomorphiaTouched && r2OrthomorphiaDiffersFromConsent)
      ) &&
      !hasConsentDifferenceReason(consentDifferenceRezept);

    if (shouldShowConsentDifferenceWarning) {
      modal.warning({
        title: CONSENT_DIFFERENT_TREATMENT_WARNING,
        okText: 'OK',
        onOk: () => {
          applyChanges();
          revealConsentDifferenceReason(consentDifferenceRezept);
        },
      });
      return;
    }

    applyChanges();
  }, [
    allDisabled,
    isHouseCall,
    isR2,
    effAcupuncture,
    effMoxibustion,
    effElectric,
    effR2NotHotTreatment,
    date,
    rawVisit?.rezept_id,
    rezepts,
    draft,
    r2MassageTouched,
    r2MassageDiffersFromConsent,
    r2MassageParts,
    r2OrthomorphiaParts,
    r2OrthomorphiaTouched,
    r2OrthomorphiaDiffersFromConsent,
    modal,
    onApply,
    onClose,
  ]);

  // BD §3.5: close (X) / キャンセル → only show confirm when user made changes
  const handleCancel = useCallback(() => {
    if (!isDirty) {
      onClose();
      return;
    }
    modal.confirm({
      title: FORM_MESSAGES.DISCARD_CHANGES_TITLE,
      content: FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION,
      okText: FORM_MESSAGES.CONFIRM_OK,
      cancelText: FORM_MESSAGES.CONFIRM_CANCEL,
      onOk: () => onClose(),
    });
  }, [isDirty, modal, onClose]);

  const handleJumpToVisitsSchedule = () => {
    // Persistence + URL both use (massager_id, clinic_location_id) instead of CLM id.
    // Canonical 3-segment URL: /visits/schedule/:massagerId/:clinicLocationId/:visitdate.
    // 2-segment fallback when no location is available: /visits/schedule/:massagerId/:visitdate
    // (VisitSchedulePage will pick the first location for that massager).
    const massagerId = current.massager_id || 0;
    const locationId = rawVisit?.clinic_location_id ?? current.clinic_location_id ?? null;
    if (massagerId > 0 && locationId !== null) {
      navigate(`/${tenant}/visits/schedule/${massagerId}/${locationId}/${date}`);
      return;
    }
    navigate(`/${tenant}/visits/schedule/${massagerId}/${date}`);
  };

  // BD 1.3.1 / 1.3.2 §1: 「{和暦年}{月}{日}　詳細編集」 — a full-width space (U+3000).
  const titleText = `${formatJapaneseDate(date)}　詳細編集`;

  const rowCellClass =
    'border border-gray-300 text-center font-medium py-1 px-3 w-[110px]';
  const checkCellClass =
    'border border-gray-300 text-center py-1 px-3';

  const treatmentRow = (
    label: string,
    bg: string,
    checked: boolean,
    onChange: (next: boolean) => void,
    isDisabled: boolean,
  ) => (
    <tr>
      <td className={rowCellClass} style={{ backgroundColor: bg }}>{label}</td>
      <td className={checkCellClass}>
        <AppCheckbox
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          disabled={isDisabled}
        />
      </td>
    </tr>
  );

  // 令和8年法改正 No 5 — the 明細書発行加算 applies to treatments from 2026-07-01, and from
  // then on a 月別 patient bills it only on the month's earliest visit, so the box is
  // hidden on every later one. Whether THIS visit is that earliest one comes from the
  // server: the flag on the visit cannot answer it, because the user may untick the
  // earliest visit and then every visit of the month reads false.
  const { data: meisaiStatus } = useMeisaiIssueVisitStatus(
    patientId,
    date >= LAW_REVISION_DATE_2026 ? date : undefined,
    typeof rawVisit?.id === 'number' ? rawVisit.id : undefined
  );
  // `is_earliest_of_month` off the response only sees saved rows. Overlaying the calendar's
  // unsaved adds and deletes is what makes an already-saved 6th give up the box the moment a
  // 5th is staged — before this the 6th kept showing it until 登録 and a reload.
  const isEarliestOfMonth = useMemo(
    () =>
      resolveIsEarliestOfMonth(meisaiStatus, stagedVisits, deletedVisitIds, {
        id: rawVisit?.id ?? date,
        visitDate: date,
      }),
    [meisaiStatus, stagedVisits, deletedVisitIds, rawVisit?.id, date]
  );

  const showMeisaiIssue = useMemo(() => {
    if (date < LAW_REVISION_DATE_2026) return false;
    // A row 訪問入力 created never offers the box — not on 訪問入力, which dropped it, and not
    // here either, under 日別 or 月別. Such a row is not even a candidate for the month's
    // earliest visit, so the 月別 charge sits on the first 来院 instead.
    if (isHouseCall) return false;
    // Until the month's state arrives, show the box rather than flash it away.
    if (!meisaiStatus) return true;
    return meisaiStatus.issue_type === MEISAI_ISSUE_TYPE_DAILY || isEarliestOfMonth;
  }, [date, isHouseCall, meisaiStatus, isEarliestOfMonth]);

  // The box shows ticked whenever it is shown at all — that is what the server derives on
  // save (日別 every visit, 月別 the month's earliest), and the box is only shown in those
  // same two cases. Saved days included: editing or copying can make a saved day become the
  // month's earliest while its stored flag is still 0. A day the operator already touched by
  // hand keeps its own value.
  //
  // Gated on `isOpen` and written functionally, both for the same reason: this modal is
  // mounted as soon as a day is selected, long before 詳細 opens it. Ungated, the default
  // landed on the closed modal and the open-time reset above then wiped it — the box came up
  // unchecked on a brand-new day. Reading `draft` from the render closure has the same hole:
  // the reset is declared first, so its setDraft is already queued when this runs and the
  // closure still holds the pre-reset value. `setDraft(prev => ...)` sees the reset row.
  //
  // It deliberately does not mark the form dirty: nothing the operator did changed, and
  // flagging it made every plain open of a fresh day prompt the discard confirm on close.
  useEffect(() => {
    if (!isOpen) return;
    const next = showMeisaiIssue ? 1 : 0;
    setDraft(prev => {
      if (prev.is_meisai_issue_user_changed) return prev;
      return (prev.is_meisai_issue ?? 0) === next ? prev : { ...prev, is_meisai_issue: next };
    });
  }, [isOpen, showMeisaiIssue]);

  const editTab = (
    <div className="flex flex-col gap-3 py-1">
      {isLocked && <p className="text-red-600 text-sm">{LOCKED_EDIT_DELETE_MESSAGE}</p>}
      {!hasConsent && (
        <p className="text-red-600 text-sm">{NO_CONSENT_FOR_TREATMENT_DATE_MESSAGE}</p>
      )}
      {hasDistanceError && (
        <p className="text-red-600 text-sm">{DISTANCE_MEASUREMENT_FAILED_MESSAGE}</p>
      )}
      <VisitErrorMessageList
        errorItems={rawVisit?.error_items}
        errorMessages={rawVisit?.error_messages}
        tenant={tenant}
        className="text-sm list-disc"
        lockedPeriodMessage={LOCKED_EDIT_DELETE_MESSAGE}
      />


      {/* BD §2.1 + §2.2: 初検料 + 初検 checkbox — only when day = first_treatment_date */}
      {!isR2 && isFirstTreatmentDay && initialInspectionPrices && (
        <div className="flex items-center gap-2 text-sm">
          <span>初検料</span>
          <AppTextField
            disabled
            value={
              String((effAcupuncture && effMoxibustion)
                ? initialInspectionPrices.combined
                : initialInspectionPrices.single)
            }
            className="w-[260px]"
          />
          <AppCheckbox
            checked={!!draft.is_initial_inspection_fee}
            onChange={e => update('is_initial_inspection_fee', e.target.checked ? 1 : 0)}
            disabled={allDisabled}
          />
        </div>
      )}

      {!isR2 && (
        <table className="border-collapse">
          <tbody>
            {treatmentRow(
              'はり',
              LABEL_BG.acupuncture,
              !!effAcupuncture,
              v => update('is_acupuncture', v ? 1 : 0),
              acupunctureDisabled,
            )}
            {treatmentRow(
              'きゅう',
              LABEL_BG.moxibustion,
              !!effMoxibustion,
              v => update('is_moxibustion', v ? 1 : 0),
              moxibustionDisabled,
            )}
            {treatmentRow(
              '電(鍼・温)',
              LABEL_BG.electric,
              !!effElectric,
              v => update('electric_treatment', v ? 1 : 0),
              electricDisabled,
            )}
          </tbody>
        </table>
      )}

      {isR2 && (
        <table className="border-collapse">
          <tbody>
            <tr>
              <td className={rowCellClass} style={{ backgroundColor: LABEL_BG.acupuncture }}>
                マッサージ
              </td>
              <td className={checkCellClass}></td>
            </tr>
            {R2_MASSAGE_PART_OPTIONS.map(part => (
              <tr key={`detail-r2-massage-${part.value}`}>
                <td className={rowCellClass} style={{ backgroundColor: LABEL_BG.acupuncture }}>
                  {part.label}
                </td>
                <td className={checkCellClass}>
                  <AppCheckbox
                    checked={r2MassageParts.has(part.value)}
                    onChange={e =>
                      toggleR2Part('massage', r2MassageParts, part.value, e.target.checked)
                    }
                    disabled={allDisabled || !hasConsent || !hasMassage || !r2MassageEnabledParts.has(part.value)}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td className={rowCellClass} style={{ backgroundColor: LABEL_BG.moxibustion }}>
                変形徒手矯正
              </td>
              <td className={checkCellClass}></td>
            </tr>
            {R2_ORTHOMORPHIA_PART_OPTIONS.map(part => (
              <tr key={`detail-r2-orthomorphia-${part.value}`}>
                <td className={rowCellClass} style={{ backgroundColor: LABEL_BG.moxibustion }}>
                  {part.label}
                </td>
                <td className={checkCellClass}>
                  <AppCheckbox
                    checked={r2OrthomorphiaParts.has(part.value)}
                    onChange={e =>
                      toggleR2Part('orthomorphia', r2OrthomorphiaParts, part.value, e.target.checked)
                    }
                    disabled={
                      allDisabled ||
                      !hasConsent ||
                      !hasMassage ||
                      !r2OrthomorphiaAllowedParts.has(part.value)
                    }
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td className={rowCellClass} style={{ backgroundColor: LABEL_BG.electric }}>
                罨法と電療
              </td>
              <td className={checkCellClass}></td>
            </tr>
            {treatmentRow(
              '温罨法',
              LABEL_BG.electric,
              r2HotTreatmentType === R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS &&
                !effR2NotHotTreatment,
              v => update('is_not_hot_treatment', !v),
              r2HotTreatmentDisabled ||
                r2HotTreatmentType !== R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS,
            )}
            {treatmentRow(
              '温罨法・電療',
              LABEL_BG.electric,
              r2HotTreatmentType === R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS_ELECTRO &&
                !effR2NotHotTreatment,
              v => update('is_not_hot_treatment', !v),
              r2HotTreatmentDisabled ||
                r2HotTreatmentType !== R2_HOT_TREATMENT_TYPE_ID.HOT_COMPRESS_ELECTRO,
            )}
          </tbody>
        </table>
      )}

      <div className="grid grid-cols-[90px_1fr] gap-x-3 gap-y-2 items-center mt-2">
        <span className="text-sm">転帰</span>
        <AppSelect
          value={draft.outcome_type_id || undefined}
          onChange={val => update('outcome_type_id', (val as number | null) || null)}
          options={outcomeOptions}
          placeholder=""
          disabled={allDisabled}
          allowClear={!isR2}
          className="w-40"
        />

        <span className="text-sm">施術担当者</span>
        <AppSelect
          value={(() => {
            const entry = massagers.find(
              m => m.massager_id === draft.massager_id
                && m.clinic_location_id === draft.clinic_location_id,
            ) ?? massagers.find(m => m.massager_id === draft.massager_id);
            return entry?.id ?? undefined;
          })()}
          onChange={val => {
            const entry = massagers.find(m => m.id === val);
            if (entry) {
              setDraft(prev => ({
                ...prev,
                massager_id: entry.massager_id,
                clinic_location_id: entry.clinic_location_id,
              }));
              setIsDirty(true);
            }
          }}
          options={massagerOptions}
          placeholder=""
          disabled={allDisabled || isHouseCall}
          className="w-full"
        />

        <span className="text-sm">施術メモ</span>
        <AppTextArea
          value={draft.massager_remarks ?? ''}
          onChange={e => update('massager_remarks', e.target.value || null)}
          maxLength={255}
          disabled={allDisabled || isLocked}
          placeholder="施術メモ"
          className="w-full"
        />

        {/* 令和8年法改正 No 5 — 明細書発行加算 (10円). Hidden before the 施行日 rather
            than shown and ignored: the 加算 did not exist for those treatment dates.
            It sits in the grid's value column, under the 施術メモ box, so it lines up with
            the fields above rather than starting at the label margin. The empty first cell
            is what keeps that column; the row has no label of its own. */}
        {showMeisaiIssue && (
          <>
            <span />
            <AppCheckbox
              checked={!!draft.is_meisai_issue}
              onChange={e => {
                update('is_meisai_issue', e.target.checked ? 1 : 0);
                update('is_meisai_issue_user_changed', 1);
              }}
              disabled={allDisabled}
              className="text-sm"
            >
              明細書発行加算
            </AppCheckbox>
          </>
        )}
      </div>
    </div>
  );

  const houseCallTab = (
    <div className="flex flex-col gap-3 py-1">
      <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 items-center text-sm">
        {/* BD §3.1: 訪問日時 — format H時mm分, read-only */}
        <span className="text-gray-600">訪問日時</span>
        <AppTextField
          disabled
          value={formatVisitTime(rawVisit?.visit_datetime_from)}
          className="w-full"
        />

        {/* BD §3.2: 所要時間 — read-only */}
        <span className="text-gray-600">所要時間</span>
        <AppTextField
          disabled
          value={formatVisitMinutes(rawVisit?.visit_minutes)}
          className="w-full"
        />

        {/* BD §3.3: 診療区分 — fixed radio 保険診療 */}
        <span className="text-gray-600">診療区分</span>
        <AppRadio checked disabled>保険診療</AppRadio>
      </div>
      <div>
        <AppButton type="primary" disabled={allDisabled} onClick={handleJumpToVisitsSchedule}>
          訪問入力画面
        </AppButton>
      </div>
    </div>
  );

  const tabItems = [
    { key: 'edit', label: '詳細編集', children: editTab },
    { key: 'houseCall', label: '往診編集', disabled: !isHouseCall, children: houseCallTab },
  ];

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={titleText}
      maxWidth="max-w-xl"
      okText="適用"
      cancelText="キャンセル"
      onOk={handleApply}
    >
      <AppTabs
        items={tabItems}
        defaultActiveKey={isHouseCall ? 'houseCall' : 'edit'}
      />
    </AppModal>
  );
};

export default VisitClinicDetailModal;
